import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Check, Copy, Key, Disc3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrLogin } from '@nostrify/react/login';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { toast } from '@/hooks/useToast';
import { nip19 } from 'nostr-tools';
import { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '@/hooks/useAppContext';

// Generate a random colorful gradient
function generateRandomGradient(): string[] {
  const gradients = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
    ['#43e97b', '#38f9d7'],
    ['#fa709a', '#fee140'],
    ['#30cfd0', '#330867'],
    ['#a8edea', '#fed6e3'],
    ['#ff9a56', '#ff6a88'],
    ['#ffecd2', '#fcb69f'],
    ['#ff6e7f', '#bfe9ff'],
    ['#e0c3fc', '#8ec5fc'],
    ['#f8b500', '#fceabb'],
    ['#fdcbf1', '#e6dee9'],
    ['#a1c4fd', '#c2e9fb'],
    ['#d299c2', '#fef9d7'],
    ['#89f7fe', '#66a6ff'],
    ['#fddb92', '#d1fdff'],
    ['#9890e3', '#b1f4cf'],
    ['#ebc0fd', '#d9ded8'],
    ['#96fbc4', '#f9f586'],
  ];

  return gradients[Math.floor(Math.random() * gradients.length)];
}

export function SettingsPage() {
  const { t, language, setLanguage } = useTranslation();
  const { user } = useCurrentUser();
  const { logins } = useNostrLogin();
  const { config, updateConfig } = useAppContext();
  const [backupDismissed, setBackupDismissed] = useState(() => {
    return localStorage.getItem('backup_dismissed') === 'true';
  });
  const [nsecKey, setNsecKey] = useState<string | null>(null);

  // Generate random gradient on component mount (page refresh)
  const gradient = useMemo(() => generateRandomGradient(), []);

  // Get the current login to access nsec
  const currentLogin = logins[0];
  const hasNsec = currentLogin?.type === 'nsec';

  // Extract nsec from localStorage when component mounts
  useEffect(() => {
    if (currentLogin?.type === 'nsec' && user) {
      try {
        // Try to get nsec from our dedicated storage key
        const storedNsec = localStorage.getItem(`nsec_${user.pubkey}`);

        if (storedNsec) {
          setNsecKey(storedNsec);
        } else {
          // Fallback: try to extract from login data
          const loginsData = localStorage.getItem('nostr-login');

          if (loginsData) {
            const parsedLogins = JSON.parse(loginsData);
            const currentLoginData = parsedLogins.find((l: { id: string }) => l.id === currentLogin.id);

            // The nsec should be stored in the login object
            if (currentLoginData?.nsec) {
              setNsecKey(currentLoginData.nsec);
              // Also save it to our dedicated storage for next time
              localStorage.setItem(`nsec_${user.pubkey}`, currentLoginData.nsec);
            } else if (currentLoginData?.secretKey) {
              // If secretKey is stored as Uint8Array or hex string
              if (typeof currentLoginData.secretKey === 'string') {
                // Hex string - convert to Uint8Array
                const bytes = new Uint8Array(currentLoginData.secretKey.match(/.{1,2}/g)?.map(byte => parseInt(byte, 16)) || []);
                const nsec = nip19.nsecEncode(bytes);
                setNsecKey(nsec);
                localStorage.setItem(`nsec_${user.pubkey}`, nsec);
              } else if (Array.isArray(currentLoginData.secretKey) || currentLoginData.secretKey instanceof Uint8Array) {
                // Already bytes
                const nsec = nip19.nsecEncode(new Uint8Array(currentLoginData.secretKey));
                setNsecKey(nsec);
                localStorage.setItem(`nsec_${user.pubkey}`, nsec);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to extract nsec from localStorage:', error);
      }
    }
  }, [currentLogin, user]);

  const handleDownloadKeys = () => {
    if (!user || !nsecKey) {
      toast({
        title: 'Error',
        description: 'Could not access your keys.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get npub
      const npub = nip19.npubEncode(user.pubkey);

      // Use the nsec from state
      const nsec = nsecKey;

      // Create file content
      const content = `NodeCast Account Keys
==================

Public Key (npub):
${npub}

Secret Key (nsec):
${nsec}

IMPORTANT: Keep your secret key (nsec) safe and private!
Never share it with anyone. This is your password to access your account.
`;

      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain; charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'nodecast-keys.txt';
      document.body.appendChild(a);
      a.click();

      // Clean up after a delay to ensure download starts
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      toast({
        title: t.settings.keysSaved,
        description: 'Your keys have been downloaded.',
      });
    } catch (error) {
      console.error('Failed to download keys:', error);
      toast({
        title: 'Download failed',
        description: 'Could not download your keys. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDismissBackup = () => {
    setBackupDismissed(true);
    localStorage.setItem('backup_dismissed', 'true');
  };

  const handleCopyNpub = () => {
    if (!user) return;
    const npub = nip19.npubEncode(user.pubkey);
    navigator.clipboard.writeText(npub);
    toast({
      title: 'Copied!',
      description: 'Public key copied to clipboard.',
    });
  };

  const backgroundStyle = {
    background: `linear-gradient(135deg, ${gradient.join(', ')})`,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Blurred gradient background */}
      <div
        className="fixed top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-45 pointer-events-none"
        style={backgroundStyle}
      />

      {/* Global Navigation */}
      <Navigation />

      {/* Settings Content */}
      <div className="relative container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t.common.back}
            </Button>
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-8">{t.settings.title}</h1>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="general">{t.settings.general}</TabsTrigger>
            <TabsTrigger value="appearance">{t.settings.appearance}</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            {/* Account Keys Section */}
            {user && (
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                        <Key className="h-5 w-5" />
                        Account Keys
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {currentLogin?.type === 'nsec'
                          ? 'Download and backup your keys for safekeeping.'
                          : 'View your public key. Your private key is managed by your browser extension.'}
                      </p>
                    </div>

                    {/* Public Key - Always visible */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Public Key (npub)</label>
                      <div className="flex gap-2">
                        <div className="flex-1 p-3 rounded-lg glass border-white/20 font-mono text-sm truncate">
                          {nip19.npubEncode(user.pubkey)}
                        </div>
                        <Button
                          onClick={handleCopyNpub}
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Download Keys - Only for nsec logins */}
                    {currentLogin?.type === 'nsec' && (
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-sm text-muted-foreground mb-3">
                          {t.settings.backupDescription}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleDownloadKeys}
                            className="gap-2"
                            disabled={!nsecKey}
                          >
                            <Download className="h-4 w-4" />
                            {t.settings.downloadKeys}
                          </Button>
                          {!backupDismissed && (
                            <Button
                              onClick={handleDismissBackup}
                              variant="ghost"
                            >
                              {t.settings.dismissBackup}
                            </Button>
                          )}
                        </div>
                        {!nsecKey && (
                          <p className="text-xs text-yellow-500 mt-2">
                            Unable to access private key. Try logging out and back in.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Login Type Info */}
                    <div className="pt-2 border-t border-white/10">
                      <p className="text-xs text-muted-foreground">
                        Login method: <span className="font-medium capitalize">{currentLogin?.type || 'Unknown'}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Language Selection */}
            <Card className="glass border-white/10 mt-6">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold mb-2">{t.settings.language}</h2>
                    <p className="text-sm text-muted-foreground">
                      {t.settings.selectLanguage}
                    </p>
                  </div>
                  <Select value={language} onValueChange={(value) => setLanguage(value as 'en' | 'es')}>
                    <SelectTrigger className="w-full glass border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <div className="flex items-center gap-2">
                            {lang.nativeName}
                            {language === lang.code && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Disc3 className="h-5 w-5" />
                      Spinning Record Player
                    </h2>
                    <Switch
                      id="spinning-records"
                      checked={config.spinningRecords ?? false}
                      onCheckedChange={(checked) => {
                        updateConfig(() => ({ spinningRecords: checked }));
                      }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Transform the bottom player into a spinning vinyl record
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
